import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

// Utils
import { pathname } from "#src/config/pathname";

// API related
import directoryService from "#src/services/endpoint/directory";
import {
  IDirectoryDepartmentWithPath,
  IDirectoryUser,
} from "#src/services/endpoint/directory/types";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";

// Components
import { ButtonV2 } from "#src/commonV2/Button";
import { InputV2 } from "#src/commonV2/inputs/Input";
import { Modal, Select } from "@lockerpm/design";

type Props = {
  workspaceId: string;
  isOpen: boolean;
  closeModal: () => void;
  selectedUser: IDirectoryUser | null;
  onSuccess: (newData: IDirectoryUser) => void;
  departments: IDirectoryDepartmentWithPath[];
};

export const EditUserModal = (props: Props) => {
  const {
    workspaceId,
    isOpen,
    closeModal,
    selectedUser,
    onSuccess,
    departments,
  } = props;
  const { t } = useTranslation("endpoint", { keyPrefix: "directory" });

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [departmentId, setDepartmentId] = useState<string>();

  // --------------- COMPUTED ---------------

  const departmentOptions = useMemo(
    () =>
      departments.map((department) => ({
        label: department.path,
        value: department.id,
      })),
    [departments]
  );

  // --------------- METHODS ---------------

  const editUser = useCallback(async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    try {
      await directoryService.editUser(workspaceId, selectedUser.id, {
        job_title: title.trim(),
        department_id: departmentId || null,
      });
      closeModal();

      // Update table
      const newDepartment = departments.find(
        (department) => department.id === departmentId
      );
      onSuccess({
        ...selectedUser,
        job_title: title.trim(),
        department: departmentId
          ? {
              id: departmentId,
              name: newDepartment?.name || "",
              parent_id: newDepartment?.parent_id || null,
              created_time: newDepartment?.created_time || 0,
            }
          : null,
      });
    } catch (error) {
      apiErrorHandler(error);
    }
    setIsLoading(false);
  }, [
    selectedUser,
    workspaceId,
    title,
    departmentId,
    closeModal,
    departments,
    onSuccess,
  ]);

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (isOpen) {
      setTitle(selectedUser?.job_title || "");
      setDepartmentId(selectedUser?.department?.id);
    }
  }, [isOpen, selectedUser]);

  // --------------- RENDER ---------------

  return (
    <Modal
      title={t("edit_user.title")}
      open={isOpen}
      footer={null}
      maskClosable={false}
      onCancel={closeModal}
      className="v2 max-w-[600px]"
      centered
      width="90%"
    >
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Department */}
        <div>
          <p className="text-sm font-medium text-gra-v2-700 mb-2">
            {t("table.columns.department")}
          </p>

          <Select
            showSearch
            onChange={setDepartmentId}
            value={departmentId}
            options={departmentOptions}
            className="w-full rounded-xl h-10 shadow-xs"
            optionFilterProp="label"
            disabled={isLoading}
            virtual={false}
            allowClear
          />

          <p className="text-sm text-gray-v2-600 mt-1.5">
            {t("edit_user.select_or")}
            <span>
              <Link
                className="text-brand-v2-600 font-semibold no-underline hover:text-brand-v2-600"
                to={generatePath(pathname.ENDPOINT_DIRECTORY_DEPARTMENTS, {
                  workspaceId,
                })}
              >
                {t("edit_user.create_new")}
              </Link>
            </span>
          </p>
        </div>
        {/* Department end */}

        {/* Title */}
        <div>
          <p className="text-sm font-medium text-gray-v2-700 mb-2">
            {t("table.columns.title")}
          </p>
          <InputV2
            disabled={isLoading}
            className="w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        {/* Title end */}
      </div>
      {/* Form end */}

      {/* Footer */}
      <div className="flex gap-2 justify-end mt-8">
        <ButtonV2
          variant="secondary"
          size="lg"
          color="gray"
          onClick={closeModal}
        >
          <p>{t("common.cancel")}</p>
        </ButtonV2>

        <ButtonV2
          variant="primary"
          size="lg"
          color="brand"
          pending={isLoading}
          onClick={editUser}
        >
          <p>{t("common.update")}</p>
        </ButtonV2>
      </div>
      {/* Footer end */}
    </Modal>
  );
};
